<template>
  <GraphqlDataTable
    v-model:order-by="orderBy"
    v-model:total-count="totalCount"
    :query="appliedContactsQuery"
    :filter-black-list
    :column-black-list
    :additional-query-variables
    :max-height-adjustment="30"
    :search-placeholder
    :sort-options="trustedContactsSortOptions"
    @row-click="$emit('row-click', $event)"
  >
    <template #action="{ row, index }">
      <AppliedContactActionsColumn
        :row="row"
        :index="index"
        @approved="$emit('approved')"
        @approve-error="$emit('approve-error', $event)"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
        @block="$emit('block', $event)"
      />
    </template>
    <template #no-results>
      <slot name="no-results">
        {{ $t('No matching records found') }}
      </slot>
    </template>
  </GraphqlDataTable>
</template>

<script lang="ts" setup>
import { computed, provide, ref } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { graphql } from '@/gql'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import AppliedContactActionsColumn from '@/components/Contacts/CompanyFreelancer/PageApplicants/AppliedContactActionsColumn.vue'
import { $t } from '@/composables/translation'
import { ContactStatus } from '@/gql/graphql'
import { DefaultStatuses } from '@/components/GraphqlDataTable/filters/trustedContacts/injection'
import {
  trustedContactsSortOptions,
  trustedContactsDefaultOrderBy,
} from '@/components/GraphqlDataTable/columns/TrustedContacts'

const defaultStatuses = ref<string[]>([ContactStatus.Applied])
provide(DefaultStatuses, defaultStatuses)

defineEmits<{
  'row-click': [row: any]
  edit: [row: any]
  delete: [row: any]
  block: [row: any]
  approved: []
  'approve-error': [errors: string[]]
}>()

withDefaults(
  defineProps<{
    searchPlaceholder?: string
  }>(),
  {
    searchPlaceholder: $t('Search name, email, job title...'),
  },
)

const orderBy = ref({ ...trustedContactsDefaultOrderBy })

const totalCount = defineModel<number | undefined>('totalCount')

const additionalQueryVariables = computed(() => ({
  companyId: currentAccount.globalId,
  accounts: [currentAccount.globalId],
  statuses: [ContactStatus.Applied],
}))

const filterBlackList = computed(() => [
  'accounts',
  'companyId',
  'states',
  'statuses',
  'origin',
])

const columnBlackList = [
  'TrustedContactJobsCountColumn',
  'TrustedContactNotesColumn',
]

const appliedContactsQuery = graphql(`
  query AppliedTrustedContactsList(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [ContactStatus!]
    $skills: [ID!]
    $hireHistory: HireHistoryFilterInput
    $hireStatus: HireStatusFilterInput
    $businessSetup: [BusinessEntityType!]
    $invitedByUsers: InvitedByInput
    $countries: [String!]
    $states: [String!]
    $orderBy: [TrustedContactOrderByClauseInput!]
    $companyId: ID!
    $trustedContactCustomFields: [CustomFieldTypeValueInput!]
  ) {
    trustedContacts(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      skills: $skills
      invitedByUsers: $invitedByUsers
      businessSetup: $businessSetup
      countries: $countries
      states: $states
      orderBy: $orderBy
      hireHistory: $hireHistory
      hireStatus: $hireStatus
      customFields: $trustedContactCustomFields
    ) {
      data {
        ...TrustedContactWorkerColumn
        ...TrustedContactSkillsColumn
        ...TrustedContactLocationColumn
        ...TrustedContactBusinessSetupColumn
        ...TrustedContactLinksColumn
      }
    }
  }
`) as TypedQueryDocumentNode
</script>
